'use client';

import { useResumeBuilderStore } from '@/stores/resume-builder-store';
import { Checkbox } from '@/components/ui/forms/checkbox';
import { Label } from '@/components/ui/forms/label';
import { Textarea } from '@/components/ui/forms/textarea';
import {
  DEFAULT_CONSENT_TEXT,
  MAX_CONSENT_TEXT_LENGTH,
} from '@/lib/resume/consent';

export default function ConsentSection() {
  const { resume, updateConsent } = useResumeBuilderStore();

  if (!resume) return null;

  const consent = resume.content.consent ?? { enabled: false, text: '' };

  const handleToggle = (checked: boolean) => {
    if (!checked) {
      // Keep the wording. Unticking is "do not print this", not "throw it
      // away" — an imported clause quotes national law the candidate chose,
      // and re-ticking must bring it back rather than the generic default.
      updateConsent({ enabled: false });
      return;
    }

    updateConsent({
      enabled: true,
      text: consent.text.trim() ? consent.text : DEFAULT_CONSENT_TEXT,
    });
  };

  return (
    <div className="space-y-4">
      <p className="text-body text-muted-foreground">
        Many EU employers, and most Polish ones, expect a data-processing consent clause on a CV.
        When enabled it is printed as fine print at the very end of your resume.
      </p>

      <div className="flex items-start gap-3">
        <Checkbox
          id="consent-enabled"
          checked={consent.enabled}
          onCheckedChange={(checked) => handleToggle(checked === true)}
          className="mt-0.5"
        />
        <Label htmlFor="consent-enabled" className="cursor-pointer leading-snug">
          Include data-processing consent
        </Label>
      </div>

      {consent.enabled && (
        <div className="space-y-2">
          <Label htmlFor="consent-text">Consent text</Label>
          <Textarea
            id="consent-text"
            value={consent.text}
            onChange={(event) => updateConsent({ text: event.target.value })}
            maxLength={MAX_CONSENT_TEXT_LENGTH}
            placeholder={DEFAULT_CONSENT_TEXT}
            rows={5}
          />
          <p className="text-caption text-muted-foreground">
            If we found a consent clause in the resume you uploaded, it is kept word for word —
            it may cite national law, such as the Polish Act of 10 May 2018, that the generic
            wording does not.
          </p>
        </div>
      )}
    </div>
  );
}
